import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";

type Focus = "leads" | "seo" | "ai" | "crm" | "scale" | "automotive" | "general";

interface NeedACarHighlightProps {
  /** Drives the angle of the copy + anchor text. Different focuses produce different varied-anchor text - important for internal-link SEO. */
  focus?: Focus;
  /** "featured" = full card with stats, "compact" = single-row inline mention. */
  variant?: "featured" | "compact";
  /** Color theme. Defaults to a neutral slate. */
  theme?: "slate" | "blue" | "amber";
  className?: string;
}

const HREF = "/case-studies/need-a-car-automotive-growth/";

const COPY: Record<Focus, { eyebrow: string; headline: string; body: string; anchor: string; stats: { value: string; label: string }[] }> = {
  leads: {
    eyebrow: "Proof in production",
    headline: "400% inbound lead growth across 6 rooftops",
    body: "Before Frayze, our founder built the lead-generation engine behind the GTA's Need A Car dealer group. From dormant database to 400% inbound growth across paid, organic, social, and database reactivation. That build is the Frayze blueprint.",
    anchor: "Read the 400% lead-growth case study",
    stats: [
      { value: "+400%", label: "Inbound leads" },
      { value: "150k+", label: "Database contacts" },
      { value: "6", label: "Rooftops" },
    ],
  },
  seo: {
    eyebrow: "Proof in production",
    headline: "600–700% monthly traffic on an in-house WordPress rebuild",
    body: "When Need A Car's third-party photo vendor and listing platforms started costing them sales, our founder rebuilt the site in-house on WordPress with a SQL inventory layer and feed distribution to AutoTrader, CarGurus, Kijiji, Facebook, and Google Merchant Center.",
    anchor: "See the in-house dealer-group site rebuild",
    stats: [
      { value: "600–700%", label: "Monthly traffic lift" },
      { value: "5+", label: "Feed channels" },
      { value: "6", label: "Rooftops" },
    ],
  },
  ai: {
    eyebrow: "Proof in production",
    headline: "AI for document collection, vehicle matching, and voice agents",
    body: "Inside the Need A Car build our founder layered AI for document collection and chasing, scrapers + AI for approval-based vehicle matching against a rolling 700-car inventory, and voice agents on ElevenLabs and Vapi.",
    anchor: "Read how the AI layer compounded the system",
    stats: [
      { value: "700", label: "Cars matched daily" },
      { value: "72", label: "Staff on AI-assisted flow" },
      { value: "6", label: "Languages handled" },
    ],
  },
  crm: {
    eyebrow: "Proof in production",
    headline: "Migrated 6 rooftops to a unified CRM in 10 days",
    body: "Before Frayze, our founder migrated Need A Car off SM360 and onto a unified GoHighLevel + custom layer with a first-of-its-kind 2-way credit integration. 72 staff onboarded live across all locations in 10 days, no beta, all on AI and automation.",
    anchor: "Read the multi-rooftop CRM migration case study",
    stats: [
      { value: "72", label: "Staff in 10 days" },
      { value: "150k+", label: "Records migrated" },
      { value: "First", label: "2-way credit sync" },
    ],
  },
  scale: {
    eyebrow: "Proof in production",
    headline: "$1.7M in marketing savings + 47% sales lift",
    body: "Once cost-per-sold-vehicle was measurable across the SQL inventory, credit, and CRM layers, $1.7M of annual ad spend was redirected away from unmeasured channels. Tech-stack costs dropped 52% in the same period.",
    anchor: "See the closed-loop attribution case study",
    stats: [
      { value: "$1.7M", label: "Marketing saved" },
      { value: "+47%", label: "Sales volume" },
      { value: "-52%", label: "Tech-stack cost" },
    ],
  },
  automotive: {
    eyebrow: "Flagship automotive build",
    headline: "Four years. Six rooftops. One revenue engine: rebuilt from the inside.",
    body: "Need A Car is the case study most agencies can't write. Frayze wasn't hired by them; it was built inside them by a former sales-and-finance rep. Eight phases of work: database reengagement, multi-language scale, 10-day live rollout, AI layer, owned stack, 2-way credit integration, dashboards, and the moonshot.",
    anchor: "Read the full Need A Car case study",
    stats: [
      { value: "+400%", label: "Leads" },
      { value: "$1.7M", label: "Saved" },
      { value: "+47%", label: "Sales" },
      { value: "-52%", label: "Tech cost" },
    ],
  },
  general: {
    eyebrow: "Proof in production",
    headline: "How we rebuilt a 6-rooftop GTA dealer group from the database up",
    body: "Four years, six rooftops, six languages, 72 staff live in 10 days. The Need A Car case study walks through every phase: database reengagement, CRM migration, AI layer, in-house WordPress rebuild, 2-way credit integration, TV dashboards, and the moonshot.",
    anchor: "Read the founder-built dealer-group case study",
    stats: [
      { value: "+400%", label: "Leads" },
      { value: "$1.7M", label: "Saved" },
      { value: "+47%", label: "Sales" },
    ],
  },
};

const THEME = {
  slate: {
    bg: "bg-slate-50 dark:bg-slate-900/40",
    border: "border-slate-200 dark:border-slate-700",
    eyebrow: "text-slate-600 dark:text-slate-400",
    statValue: "text-slate-900 dark:text-slate-100",
    statLabel: "text-slate-500 dark:text-slate-400",
    cta: "text-slate-900 dark:text-slate-100 hover:text-blue-600",
    badge: "bg-white text-blue-700 border-blue-300",
  },
  blue: {
    bg: "bg-blue-50 dark:bg-blue-950/40",
    border: "border-blue-200 dark:border-blue-800",
    eyebrow: "text-blue-700 dark:text-blue-300",
    statValue: "text-blue-700 dark:text-blue-300",
    statLabel: "text-slate-600 dark:text-slate-300",
    cta: "text-blue-700 dark:text-blue-300 hover:text-blue-900",
    badge: "bg-white text-blue-700 border-blue-300",
  },
  amber: {
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800",
    eyebrow: "text-amber-700 dark:text-amber-300",
    statValue: "text-amber-700 dark:text-amber-300",
    statLabel: "text-slate-600 dark:text-slate-300",
    cta: "text-amber-700 dark:text-amber-300 hover:text-amber-900",
    badge: "bg-white text-amber-700 border-amber-300",
  },
};

export const NeedACarHighlight = ({
  focus = "general",
  variant = "featured",
  theme = "slate",
  className = "",
}: NeedACarHighlightProps) => {
  const c = COPY[focus];
  const t = THEME[theme];

  if (variant === "compact") {
    return (
      <Link
        to={HREF}
        className={`group flex items-start gap-4 rounded-xl border p-5 transition-all hover:-translate-y-0.5 hover:shadow-md ${t.bg} ${t.border} ${className}`}
      >
        <Award className={`w-5 h-5 mt-0.5 shrink-0 ${t.eyebrow}`} />
        <div className="flex-1">
          <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${t.eyebrow}`}>{c.eyebrow}</p>
          <p className="text-base font-semibold text-foreground leading-snug">{c.headline}</p>
          <p className={`mt-2 inline-flex items-center gap-1 text-sm font-bold ${t.cta}`}>
            {c.anchor} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </p>
        </div>
      </Link>
    );
  }

  return (
    <div className={`rounded-2xl border p-6 md:p-8 ${t.bg} ${t.border} ${className}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <p className={`text-xs font-bold uppercase tracking-widest ${t.eyebrow}`}>{c.eyebrow}</p>
      </div>
      <h3 className="text-xl md:text-2xl font-bold leading-tight mb-3 text-foreground">{c.headline}</h3>
      <p className="text-base text-muted-foreground leading-relaxed mb-5">{c.body}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {c.stats.map((s) => (
          <div key={s.label}>
            <div className={`text-2xl md:text-3xl font-black ${t.statValue}`}>{s.value}</div>
            <div className={`text-xs uppercase tracking-widest ${t.statLabel}`}>{s.label}</div>
          </div>
        ))}
      </div>

      <Link
        to={HREF}
        className={`inline-flex items-center gap-1.5 text-sm md:text-base font-bold group ${t.cta}`}
      >
        {c.anchor}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
};

export default NeedACarHighlight;
